/**
 * Qualitative items service — unified items endpoint family.
 *
 * The dashboard talks to customer-api's
 *   POST /qualitative-studies/{study_id}/qualitative-items/upload-document
 *
 * This is the Phase 1 entry point for non-conversational uploads
 * (PDF/DOCX/TXT — blog, article, report). The backend stores each upload
 * as a `Qualitative_Items` row with `item_type="document"`, extracts
 * paragraphs synchronously, and returns a focused subset (id, item_type,
 * paragraph_count, blob_storage_url, …) for the UI to confirm + display.
 *
 * The legacy `/qualitative-studies/{id}/documents/upload` flow (which
 * writes the separate `Qualitative_Documents` collection) is NOT used
 * here — that path stays around for the legacy Document Analysis lens
 * and is retired in Phase 5.
 */
import axiosServices from '@utils/axios';

const backendUrl = import.meta.env.VITE_BACKEND_URL;

/**
 * Upload one non-conversational document and create a document-shaped
 * `Qualitative_Items` row for it. Accepts `.pdf`, `.docx`, `.txt`
 * (matching the backend validator) up to 50 MB.
 *
 * Optional PII redaction mirrors the transcript-upload toggle. When
 * `redactPii` is true the backend runs each extracted paragraph through
 * the LLM with the same policy + substitution semantics the worker
 * uses for transcript text uploads, then stores the redacted text.
 * Redaction failures abort the upload with a 422 so PII can't leak
 * through silently.
 *
 * @param {object} params
 * @param {string} params.studyId
 * @param {File}   params.file              Browser File from the dropzone.
 * @param {boolean} [params.redactPii]      Apply PII redaction inline.
 * @param {'entity_name'|'hash'} [params.redactionSubstitution]
 * @param {string[]} [params.redactionPolicies]  Policy names to redact
 *                                               (e.g. `['person_name',
 *                                               'phone_number']`).
 * @param {(percent:number)=>void} [params.onUploadProgress]
 * @returns {Promise<{
 *   id: string,
 *   study_id: string,
 *   type: string,
 *   item_type: string,
 *   name: string,
 *   original_filename: string,
 *   file_content_type: string,
 *   blob_storage_url: string,
 *   paragraph_count: number,
 *   redaction_applied: boolean,
 *   redaction_substitution: string|null,
 *   created_at: string,
 *   updated_at: string,
 * }>}
 */
export const uploadDocumentAsQualitativeItem = async ({
  studyId,
  file,
  redactPii = false,
  redactionSubstitution,
  redactionPolicies,
  onUploadProgress,
}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (redactPii) {
    formData.append('redact_pii', 'true');
    if (redactionSubstitution) {
      formData.append('redaction_substitution', redactionSubstitution);
    }
    // FastAPI accepts repeated form fields as List[str]; append each
    // policy as its own entry rather than CSV-encoding.
    if (Array.isArray(redactionPolicies)) {
      for (const policy of redactionPolicies) {
        if (policy) formData.append('redaction_policy_names', policy);
      }
    }
  }

  const { data } = await axiosServices.post(
    `${backendUrl}/qualitative-studies/${studyId}/qualitative-items/upload-document`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onUploadProgress
        ? (evt) => {
            if (!evt.total) return;
            onUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        : undefined,
    },
  );
  return data;
};
